// Pre-reveal waiting view of the build preview (BuildChat LivePreview): the "magic at work" field in the
// empty hero slot, the AI stage stepper and the mind map button. Stages come only from the reduced
// course_build_* feed (no timers), so a replay after a reload repaints the same stepper.
import React, { useEffect, useState } from "react";
import i18n from "../../../locales/i18n";
import MindmapService from "../../services/mindmapService";
import { BUILD_STEPS, truncateWords } from "./buildMode";
import { WAIT_QUOTES } from "./waitQuotes";

const t = (k, o) => i18n.t(`aideaChat.buildWait.${k}`, o);
export const MINDMAP_POLL_MS = 10000;
export const ROTATE_MS = 9000;
const TYPE_MS = 50; // +2 chars a tick ≈ 40 chars/s: a 280-char narration snippet types out in ~7 s
// [left %, top %, delay s] of the ✦ glints.
const SPARKS = [
  [12, 20, 0],
  [80, 18, 0.9],
  [28, 74, 1.7],
  [88, 66, 2.5],
  [56, 10, 1.3],
  [16, 48, 2.1],
  [66, 82, 0.5],
];

// planning (course_build_started) → style (style_bible) → voice (narration step) → scene 1 (agent
// plan…fix phases / images…saving steps) → scene 1 ready → pause (mw paused) → remaining scenes.
export function buildStages(feed) {
  const ids = ["plan", "style", "voice", "scene", "ready"];
  if (feed.paused) ids.push("pause");
  if (feed.total > 1) ids.push("rest");
  const live = feed.live;
  let at = 0; // the current stage
  if (feed.ready.length > 0) {
    at = feed.paused ? ids.indexOf("pause") : feed.done ? ids.length : ids.indexOf("ready") + 1;
  } else if (
    live &&
    ((live.phase && live.phase !== "style_bible") ||
      (BUILD_STEPS.includes(live.step) && live.step !== "narration"))
  ) {
    at = 3;
  } else if (live) {
    at = 2; // style_bible lands once the look is written; narration starts the voice
  } else if (feed.total > 0 || feed.planned.length > 0) {
    at = 1;
  }
  const running = !feed.done || !!feed.paused;
  return ids.map((id, k) => ({
    id,
    state: k < at ? "done" : k === at && running ? "current" : "pending",
  }));
}

// Polls quietly (a 404 is "not ready yet": no toast, no auth modal) until the map exists.
// ponytail: fixed 10 s poll while the build runs; stops on ready, unmount or build end.
export function useMindmapReady(active) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready || !active) return undefined;
    let live = true;
    let timer = 0;
    const check = () =>
      MindmapService.getTrainingMindmap({ quiet: true })
        .then(
          (m) => !!(m && m.viewerUrl),
          () => false
        )
        .then((ok) => {
          if (!live) return;
          if (ok) setReady(true);
          else timer = setTimeout(check, MINDMAP_POLL_MS);
        });
    check();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [active, ready]);
  return ready;
}

// stage id (buildStages) → index of the agent working on it; an unknown stage animates every agent softly.
const AGENTS = ["planner", "art", "narrator", "designer"];
const STAGE_AGENT = { plan: 0, style: 1, voice: 2, scene: 3, ready: 3, pause: 3, rest: 3 };

// "Agents at work": chips with a status dot and two rotating activity lines (CSS only), lit by the stage.
export function AgentCluster({ stage, reduce, compact }) {
  const at = STAGE_AGENT[stage];
  return (
    <div
      className="aidea-agents"
      data-testid="agent-cluster"
      data-compact={compact ? "true" : undefined}
      data-static={reduce ? "true" : undefined}
      aria-hidden="true"
    >
      {!reduce && !compact && <span className="aidea-agents-flow" />}
      {AGENTS.map((id, k) => {
        const state = at === undefined ? "soft" : k < at ? "done" : k === at ? "active" : "idle";
        return (
          <div key={id} className="aidea-agent" data-agent={id} data-state={state}>
            <span className="aidea-agent-dot">{state === "done" ? "✓" : null}</span>
            <span className="aidea-agent-name">{t(`agents.${id}.name`)}</span>
            {!compact && (
              <span className="aidea-agent-lines">
                <span>{t(`agents.${id}.a`)}</span>
                {!reduce && <span>{t(`agents.${id}.b`)}</span>}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// "While you wait" panels, from real build data only (a source with nothing to show adds no panel):
// the narration of the scene in flight (brief), other Aidea flows (mw recipe-wizard cards, current one
// excluded), the models the user picked (/newchat gear), and the quotes (always).
export function waitPanels({ brief, index = 0, flows, currentFlow }) {
  const out = [];
  const scene = brief && brief.scenes && brief.scenes[index];
  if (scene)
    out.push({
      kind: "narration",
      n: index + 1,
      title: scene.title,
      text: truncateWords(scene.narration),
    });
  const other = (flows || []).filter((f) => f.id !== currentFlow);
  if (other.length) out.push({ kind: "flows", items: other });
  if (brief && brief.models && brief.models.length)
    out.push({ kind: "models", items: brief.models });
  out.push({ kind: "quotes", items: WAIT_QUOTES });
  return out;
}

// Calm typewriter; the full text is read once by screen readers, the typing is hidden from them.
// ponytail: the interval idles at the end until the slide unmounts (≤ ROTATE_MS).
function TypeOut({ text, reduce }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (reduce) return undefined;
    setN(0);
    const id = setInterval(() => setN((k) => Math.min(k + 2, text.length)), TYPE_MS);
    return () => clearInterval(id);
  }, [text, reduce]);
  return (
    <>
      <span className="aidea-wait-sr">{text}</span>
      <span aria-hidden="true" data-testid="wait-typed">
        {reduce ? text : text.slice(0, n)}
        {!reduce && n < text.length && <span className="aidea-wait-caret" />}
      </span>
    </>
  );
}

const quoteLang = () => (String(i18n.language || "").startsWith("tr") ? "tr" : "en");

function WaitSlide({ panel, round, reduce }) {
  const w = (k, o) => t(`wait.${k}`, o);
  if (panel.kind === "narration")
    return (
      <>
        <span className="aidea-wait-eyebrow">{w("narration", { n: panel.n })}</span>
        <p className="aidea-wait-body" data-kind="narration">
          <TypeOut text={panel.text} reduce={reduce} />
        </p>
        <span className="aidea-wait-foot">{panel.title}</span>
      </>
    );
  if (panel.kind === "flows") {
    const f = panel.items[round % panel.items.length];
    return (
      <>
        <span className="aidea-wait-eyebrow">{w("flows")}</span>
        <strong className="aidea-wait-title">{f.name}</strong>
        <p className="aidea-wait-body">{f.whenToUse}</p>
      </>
    );
  }
  if (panel.kind === "models")
    return (
      <>
        <span className="aidea-wait-eyebrow">{w("models")}</span>
        <ul className="aidea-wait-models">
          {panel.items.map((m) => (
            <li key={m.role}>
              <span>{w(`roles.${m.role}`)}</span>
              <span>
                {m.model}
                {m.effort ? ` · ${w(`effort.${m.effort}`)}` : ""}
              </span>
            </li>
          ))}
        </ul>
      </>
    );
  const q = panel.items[round % panel.items.length];
  const l = quoteLang();
  return (
    <>
      <span className="aidea-wait-eyebrow">{w("quote")}</span>
      <blockquote className="aidea-wait-quote">
        <p className="aidea-wait-body">“{q.text[l]}”</p>
        <footer className="aidea-wait-foot">
          — {q.author[l]}, <cite>{q.source[l]}</cite>
        </footer>
      </blockquote>
    </>
  );
}

// One fixed-size card; auto-advances every ROTATE_MS (each pass shows the next flow / quote), holds on
// hover or keyboard focus. Reduced motion: no auto-advance, no typing; the dots still switch panels.
// No aria-live here: a rotating region would talk over the build status every 9 s.
export function WaitCarousel({ panels, reduce }) {
  const [turn, setTurn] = useState(0);
  const [hold, setHold] = useState(false);
  const len = panels.length;
  useEffect(() => {
    if (reduce || hold) return undefined;
    const id = setTimeout(() => setTurn((x) => x + 1), ROTATE_MS);
    return () => clearTimeout(id);
  }, [turn, hold, reduce]);
  const slot = turn % len;
  const round = Math.floor(turn / len);
  const panel = panels[slot];
  return (
    <div
      className="aidea-wait"
      role="region"
      aria-roledescription="carousel"
      aria-label={t("wait.title")}
      data-testid="wait-carousel"
      onMouseEnter={() => setHold(true)}
      onMouseLeave={() => setHold(false)}
      onFocus={() => setHold(true)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setHold(false)}
    >
      <div
        key={turn}
        className="aidea-wait-slide"
        role="group"
        aria-roledescription="slide"
        aria-label={t("wait.slide", { n: slot + 1, total: len })}
        data-kind={panel.kind}
      >
        <WaitSlide panel={panel} round={round} reduce={reduce} />
      </div>
      {len > 1 && (
        <div className="aidea-wait-dots">
          {panels.map((p, k) => (
            <button
              key={p.kind}
              type="button"
              className="aidea-wait-dot"
              aria-label={t(`wait.kind.${p.kind}`)}
              aria-current={k === slot ? "true" : undefined}
              onClick={() => setTurn(round * len + k)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Aurora field in the purple tokens + drifting orbs + ✦ glints; reduced motion = static gradient + text.
export function MagicWait({ headline, reduce, stage, panels }) {
  const hasPanels = !!(panels && panels.length);
  return (
    <div
      className="aidea-build-magic"
      data-testid="build-magic"
      data-static={reduce ? "true" : undefined}
    >
      {!reduce && (
        <div className="aidea-build-magic-fx" aria-hidden="true">
          <span className="aidea-build-magic-orb" />
          <span className="aidea-build-magic-orb" />
          <span className="aidea-build-magic-orb" />
          {SPARKS.map(([x, y, d]) => (
            <span
              key={`${x}-${y}`}
              className="aidea-build-magic-spark"
              style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${d}s` }}
            >
              ✦
            </span>
          ))}
        </div>
      )}
      <AgentCluster stage={stage} reduce={reduce} />
      <div className="aidea-build-magic-text">
        <span aria-live="polite">
          <span key={headline} className="aidea-build-magic-headline">
            {headline}
          </span>
        </span>
        {!reduce && !hasPanels && <span className="aidea-build-magic-line" aria-hidden="true" />}
        {hasPanels && <WaitCarousel panels={panels} reduce={reduce} />}
      </div>
    </div>
  );
}

export function StageStepper({ stages, mindmap }) {
  return (
    <div className="aidea-build-wait">
      <ol className="aidea-build-stages" aria-label={t("stagesTitle")}>
        {stages.map((s) => (
          <li
            key={s.id}
            data-state={s.state}
            aria-current={s.state === "current" ? "step" : undefined}
          >
            <span className="aidea-build-stage-dot" aria-hidden="true">
              {s.state === "done" ? "✓" : null}
            </span>
            {t(`stages.${s.id}`)}
          </li>
        ))}
      </ol>
      {mindmap}
    </div>
  );
}

// Mind map toggle: preparing (disabled, spinner) until the poll finds it; then opens it in the hero slot.
export function MindmapButton({ ready, open, onToggle }) {
  return (
    <button
      type="button"
      className="aidea-build-btn aidea-build-btn-ghost aidea-build-mindmap-btn"
      disabled={!ready}
      aria-expanded={ready ? open : undefined}
      onClick={onToggle}
    >
      {ready ? (
        <>🧠 {open ? t("mindmapClose") : t("mindmap")}</>
      ) : (
        <>
          <span className="aidea-build-spinner" aria-hidden="true" />
          {t("mindmapPreparing")}
        </>
      )}
    </button>
  );
}

function sceneState(feed, index) {
  if (feed.ready.some((r) => r.index === index)) return "ready";
  if (feed.failed.includes(index)) return "failed";
  return feed.live && feed.live.index === index ? "building" : "pending";
}

// Compact progress: counter + the scene building now + one dot per planned scene (the titles live in the brief).
export function SceneStrip({ feed }) {
  if (!feed.planned.length) return null;
  const states = feed.planned.map((p) => sceneState(feed, p.index));
  const cur = feed.planned[states.indexOf("building")];
  return (
    <div className="aidea-build-preview-plan">
      <div className="aidea-build-preview-plan-line">
        <strong>
          {t("progress", {
            done: states.filter((s) => s === "ready").length,
            total: states.length,
          })}
        </strong>
        {cur && <span>{truncateWords(cur.title, 60)}</span>}
      </div>
      <ol>
        {feed.planned.map((p, i) => (
          <li
            key={p.navId}
            data-state={states[i]}
            title={p.title}
            aria-label={t("progressDot", { n: i + 1, title: p.title })}
          />
        ))}
      </ol>
    </div>
  );
}
